import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type RouteBackedTabsOptions<T extends string> = {
  basePath: string;
  defaultTab: T;
  availableTabs: readonly T[];
  segmentToTab?: Partial<Record<string, T>>;
  tabToSegment?: Partial<Record<T, string>>;
  preserveSearch?: boolean;
};

function normalizePath(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

function getSegmentAfterBase(pathname: string, basePath: string): string | null {
  const normalizedPath = normalizePath(pathname);
  const normalizedBasePath = normalizePath(basePath);

  if (normalizedPath === normalizedBasePath) {
    return null;
  }

  if (!normalizedPath.startsWith(`${normalizedBasePath}/`)) {
    return null;
  }

  const remainder = normalizedPath.slice(normalizedBasePath.length + 1);
  const [segment] = remainder.split("/");
  return segment || null;
}

export function useRouteBackedTabs<T extends string>({
  basePath,
  defaultTab,
  availableTabs,
  segmentToTab,
  tabToSegment,
  preserveSearch = false,
}: RouteBackedTabsOptions<T>) {
  const location = useLocation();
  const navigate = useNavigate();
  const normalizedBasePath = useMemo(() => normalizePath(basePath), [basePath]);

  const activeTab = useMemo(() => {
    const segment = getSegmentAfterBase(location.pathname, normalizedBasePath);
    if (!segment) {
      return defaultTab;
    }

    const mappedTab =
      segmentToTab?.[segment] ??
      (availableTabs.includes(segment as T) ? (segment as T) : null);

    if (!mappedTab || !availableTabs.includes(mappedTab)) {
      return defaultTab;
    }

    return mappedTab;
  }, [
    availableTabs,
    defaultTab,
    location.pathname,
    normalizedBasePath,
    segmentToTab,
  ]);

  const handleSelect = useCallback(
    (nextTabKey: string | null) => {
      if (!nextTabKey) {
        return;
      }

      const nextTab = nextTabKey as T;
      if (!availableTabs.includes(nextTab)) {
        return;
      }

      const nextSegment = tabToSegment?.[nextTab] ?? nextTab;
      navigate(
        {
          pathname: `${normalizedBasePath}/${nextSegment}`,
          search: preserveSearch ? location.search : "",
        },
        { replace: false },
      );
    },
    [availableTabs, location.search, navigate, normalizedBasePath, preserveSearch, tabToSegment],
  );

  return {
    activeTab,
    handleSelect,
  };
}

export default useRouteBackedTabs;